import React, { useState, useEffect } from 'react';
import { useAPI } from 'api';
import { LicenseRequest } from 'types';

const LicenseRequestHistory = () => {
	const [pendingRequests, setPendingRequests] = useState<LicenseRequest[]>([]);
	const [processedRequests, setProcessedRequests] = useState<LicenseRequest[]>([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [activeTab, setActiveTab] = useState<'pending' | 'processed'>('pending');
	const api = useAPI();

	useEffect(() => {
		fetchRequests();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const fetchRequests = async () => {
		try {
			setLoading(true);
			setError(null);

			const [pendingResponse, processedResponse] = await Promise.all([
				api.get<LicenseRequest[]>('/api/system/licensing/requests'),
				api.get<LicenseRequest[]>('/api/system/licensing/requests/processed')
			]);

			setPendingRequests(pendingResponse || []);
			setProcessedRequests(processedResponse || []);
		} catch (err) {
			setError((err as Error).message || 'Failed to fetch license requests');
			console.error('Error fetching license requests:', err);
		} finally {
			setLoading(false);
		}
	};

	const processRequests = async () => {
		try {
			setError(null);
			await api.post('/api/system/licensing/requests/process', {});
			await fetchRequests(); // Refresh the data
		} catch (err) {
			setError((err as Error).message || 'Failed to process requests');
			console.error('Error processing requests:', err);
		}
	};

	const formatDate = (dateString: string | undefined) => {
		if (!dateString) return 'N/A';
		return new Date(dateString).toLocaleString();
	};

	const getRequestStatus = (request: LicenseRequest) => {
		if (request.success) return 'Success';
		else if (request.error) return 'Error';
		else if (request.processedAt) return 'Processed';
		else return 'Pending';
	};

	const getRequestTypeLabel = (type: string) => {
		switch (type) {
			case 'auth': return 'Authorization';
			case 'deauth': return 'Deauthorization';
			case 'refresh': return 'Refresh';
			default: return type;
		}
	};

	if (loading) {
		return <div>Loading license request history...</div>;
	}

	return (
		<div className="license-request-history">
			{error && (
				<div className="error-message">
					{error}
					<button onClick={() => setError(null)} className="button-link">
						×
					</button>
				</div>
			)}

			<div className="history-header">
				<h3>License Request History</h3>
				<div className="history-actions">
					<button
						onClick={processRequests}
						className="process-button"
						title="Process pending license requests"
					>
						Process Pending Requests
					</button>
					<button
						onClick={fetchRequests}
						className="refresh-button"
						title="Refresh request history"
					>
						Refresh
					</button>
				</div>
			</div>

			<div className="history-tabs">
				<button
					className={`tab ${activeTab === 'pending' ? 'active' : ''}`}
					onClick={() => setActiveTab('pending')}
				>
					Pending ({pendingRequests.length})
				</button>
				<button
					className={`tab ${activeTab === 'processed' ? 'active' : ''}`}
					onClick={() => setActiveTab('processed')}
				>
					Processed ({processedRequests.length})
				</button>
			</div>

			<div className="requests-content">
				{activeTab === 'pending' && (
					<div className="pending-requests">
						{pendingRequests.length === 0 ? (
							<div className="no-requests">
								<p>No pending license requests</p>
							</div>
						) : (
							<div className="requests-list">
								{pendingRequests.map((request) => (
									<div key={request.id} className="request-item pending">
										<div className="request-header">
											<span className="request-type">{getRequestTypeLabel(request.type)}</span>
											<span className="request-status pending">Pending</span>
										</div>
										<div className="request-details">
											<div className="request-key">
												<strong>Key:</strong> {request.key}
											</div>
											<div className="request-info">
												<span>Created: {formatDate(request.createdAt)}</span>
												<span>Machine: {request.machineName}</span>
												<span>Domain: {request.machineDomain}</span>
											</div>
										</div>
									</div>
								))}
							</div>
						)}
					</div>
				)}

				{activeTab === 'processed' && (
					<div className="processed-requests">
						{processedRequests.length === 0 ? (
							<div className="no-requests">
								<p>No processed license requests</p>
							</div>
						) : (
							<div className="requests-list">
								{processedRequests.map((request) => (
									<div key={request.id} className="request-item processed">
										<div className="request-header">
											<span className="request-type">{getRequestTypeLabel(request.type)}</span>
											<span className={"request-status " + (request.error ? "error" : "processed")}>{getRequestStatus(request)}</span>
										</div>
										<div className="request-details">
											<div className="request-key">
												<strong>Key:</strong> {request.key}
											</div>
											<div className="request-info">
												<span>Created: {formatDate(request.createdAt)}</span>
												<span>Processed: {formatDate(request.processedAt)}</span>
												<span>Machine: {request.machineName}</span>
												<span>Domain: {request.machineDomain}</span>
											</div>
											{request.error && (
												<div className="request-error">
													<strong>Error:</strong> {request.error}
												</div>
											)}
										</div>
									</div>
								))}
							</div>
						)}
					</div>
				)}
			</div>
		</div>
	);
};

export default LicenseRequestHistory;
